import { useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import RouteErrorBoundary from "../../components/shared/RouteErrorBoundary";
import { createWorkforceRoutes } from "../../routes/workforceRoutes";
import { SetupPage, WorkforceSettingsLayout } from "../../routes/workforceLazyComponents";
import { ROUTES } from "../../components/shared/constant";
import { PackGate, WorkforcePackProvider } from "./packs/WorkforcePackContext";

/**
 * Redirect an old WLM path to its consolidated tabbed route, merging the
 * target's ?tab= with the caller's existing search so ?instanceid= survives.
 * Exported for tests. (Slice A)
 */
export function WorkforceRedirect({ to }: { to: string }) {
  const location = useLocation();
  const [path, tabQuery = ""] = to.split("?");
  const params = new URLSearchParams(location.search);
  new URLSearchParams(tabQuery).forEach((v, k) => params.set(k, v));
  const search = params.toString();
  return (
    <Navigate
      to={path + (search ? `?${search}` : "")}
      replace
    />
  );
}

const WORKFORCE_REDIRECTS: Array<[string, string]> = [
  [ROUTES.WORKFORCE_DIRECTORY, ROUTES.WORKFORCE_PEOPLE + "?tab=directory"],
  [ROUTES.WORKFORCE_ROLES, ROUTES.WORKFORCE_PEOPLE + "?tab=roles"],
  [ROUTES.WORKFORCE_ACCESS_REQUESTS, ROUTES.WORKFORCE_ACCESS + "?tab=requests"],
  [ROUTES.WORKFORCE_CERTIFICATIONS, ROUTES.WORKFORCE_ACCESS + "?tab=certification"],
  [ROUTES.WORKFORCE_ASSET_TYPES, ROUTES.WORKFORCE_ASSETS + "?tab=types"],
];

/**
 * Route tree for the Workforce Lifecycle Management module. Rendered by
 * AppLayoutContent when the pathname is under /workforce and the gate passes.
 * WorkforcePackProvider + PackGate enforce first-run sector selection.
 */
export function WorkforceRoutes() {
  const routes = useMemo(() => createWorkforceRoutes(), []);
  const location = useLocation();

  return (
    <WorkforcePackProvider>
      <PackGate>
        <Routes>
          <Route
            path={ROUTES.WORKFORCE_SETUP + ROUTES.QUERYPARAMS}
            element={
              <RouteErrorBoundary fallbackTitle="Error loading Setup">
                <SetupPage />
              </RouteErrorBoundary>
            }
          />
          {/* Splat so the settings layout can render its own nested tab routes. */}
          <Route
            path={ROUTES.WORKFORCE_SETTINGS + "/*"}
            element={
              <RouteErrorBoundary fallbackTitle="Error loading Workforce settings">
                <WorkforceSettingsLayout />
              </RouteErrorBoundary>
            }
          />
          {routes.map((route) => (
            <Route
              key={route.name}
              path={route.path}
              element={
                <RouteErrorBoundary fallbackTitle={`Error loading ${route.name}`}>
                  <route.component />
                </RouteErrorBoundary>
              }
            />
          ))}
          {WORKFORCE_REDIRECTS.map(([from, to]) => (
            <Route
              key={from}
              path={from}
              element={<WorkforceRedirect to={to} />}
            />
          ))}
          {/* /workforce and any unknown /workforce/* path land on People */}
          <Route
            path="*"
            element={
              <Navigate
                to={ROUTES.WORKFORCE_PEOPLE + location.search}
                replace
              />
            }
          />
        </Routes>
      </PackGate>
    </WorkforcePackProvider>
  );
}
